import { productionDictionaries } from "../data/production_dictionaries";
import { toAscii, tokenize } from "./tokenizer";
import { matchDictionaries } from "./dictionary_matcher";

export interface ExtractedEntities {
  employee?: string;
  equipment?: string;
  location?: string;
  failure?: string;
  arrangement?: string;
  date?: string;
  priority?: string;
  topic?: string;
}

const fallbackVerbs = [
  "cieknie", "przegrzewa", "przegrza",
  "nie działa", "nie dziala",
  "zatrzymał", "zatrzymała",
  "zacięła", "zaciala",
  "zapchał", "zapchala",
  "pęka", "peka",
  "pęknięcia", "peknencia",
  "nie płucze", "nie plucze",
  "nie zgrzewa", "nie zgrzewa",
  "rozwarstwia",
  "zanieczyszczony", "zanieczyszczona",
  "zabrudzony", "zabrudzona",
  "zepsuł", "zepsula",
  "nie trzyma", "brak"
];


function buildEmployeeMap(): Record<string, string> {
  const map: Record<string, string> = {};
  for (const e of productionDictionaries.shared.employees) {
    for (const f of e.forms) {
      map[f.toLowerCase()] = e.canonical;
      map[toAscii(f).toLowerCase()] = e.canonical;
    }
  }
  return map;
}

function buildDictionarySet(industry: "galvo" | "kosbud") {
  const company = productionDictionaries.companies[industry];
  const equipment: Record<string, string> = {};
  const equipmentBases: string[] = [];

  for (const item of company.equipment) {
    equipment[item.toLowerCase()] = item;
    equipment[toAscii(item).toLowerCase()] = item;
    equipmentBases.push(item);
  }

  for (const [canonical, synonyms] of Object.entries(company.equipmentSynonyms)) {
    equipment[canonical.toLowerCase()] = canonical;
    equipment[toAscii(canonical).toLowerCase()] = canonical;
    for (const syn of synonyms as string[]) {
      equipment[syn.toLowerCase()] = canonical;
      equipment[toAscii(syn).toLowerCase()] = canonical;
    }
  }

  const locations: Record<string, string> = {};
  for (const loc of company.locations) {
    locations[loc.toLowerCase()] = loc;
    locations[toAscii(loc).toLowerCase()] = loc;
  }

  for (const [canonical, synonyms] of Object.entries((company as any).locationSynonyms ?? {})) {
    locations[canonical.toLowerCase()] = canonical;
    locations[toAscii(canonical).toLowerCase()] = canonical;
    for (const syn of synonyms as string[]) {
      locations[syn.toLowerCase()] = canonical;
      locations[toAscii(syn).toLowerCase()] = canonical;
    }
  }

  const failures: Record<string, string> = {};
  for (const f of company.failureTypes) {
    failures[f.toLowerCase()] = f;
    failures[toAscii(f).toLowerCase()] = f;
  }

  const topics: Record<string, string> = {};
  for (const t of productionDictionaries.shared.topics) {
    topics[t.toLowerCase()] = t;
    topics[toAscii(t).toLowerCase()] = t;
  }

  return { equipment, equipmentBases, locations, failures, topics };
}

function isNumberToken(token: string): boolean {
  return /^[0-9]+$/.test(token);
}

function findLongestMatch(
  tokens: string[],
  dictionary: Record<string, string>,
  maxN: number = 5
): { key: string; start: number; end: number; value: string } | null {
  for (let n = maxN; n >= 1; n--) {
    for (let i = 0; i + n <= tokens.length; i++) {
      const phrase = tokens.slice(i, i + n).join(" ");
      const asciiPhrase = toAscii(phrase).toLowerCase();
      if (dictionary[phrase]) {
        return { key: phrase, start: i, end: i + n, value: dictionary[phrase] };
      }
      if (dictionary[asciiPhrase]) {
        return { key: asciiPhrase, start: i, end: i + n, value: dictionary[asciiPhrase] };
      }
    }
  }
  return null;
}

function extractEmployee(tokens: string[], employees: Record<string, string>): string | undefined {
  for (let i = 0; i < tokens.length; i++) {
    const key = tokens[i].toLowerCase();
    const asciiKey = toAscii(key);
    if (employees[key]) return employees[key];
    if (employees[asciiKey]) return employees[asciiKey];
  }
  return undefined;
}

function extractEquipment(tokens: string[], dict: ReturnType<typeof buildDictionarySet>): string | undefined {
  const match = findLongestMatch(tokens, dict.equipment, 5);
  if (!match) return undefined;

  const end = match.end;
  let value = match.value;

  if (end < tokens.length && isNumberToken(tokens[end])) {
    value = `${value} ${tokens[end]}`;
  }

  return value;
}

function extractLocation(tokens: string[], dict: ReturnType<typeof buildDictionarySet>): string | undefined {
  const match = findLongestMatch(tokens, dict.locations, 5);
  if (!match) return undefined;

  const end = match.end;
  let value = match.value;

  // Tak samo jak przy sprzęcie — "hala 3" / "linia 2" ma sens tylko z numerem,
  // bez tego dokładny numer hali ginie, mimo że został wypowiedziany.
  if (end < tokens.length && isNumberToken(tokens[end])) {
    value = `${value} ${tokens[end]}`;
  }

  return value;
}

function extractFailureFromDictionary(tokens: string[], dict: ReturnType<typeof buildDictionarySet>): string | undefined {
  const match = findLongestMatch(tokens, dict.failures, 6);
  return match?.value;
}

function extractFailureFallback(text: string): string | undefined {
  const normalized = text.toLowerCase();
  for (const verb of fallbackVerbs) {
    const idx = normalized.indexOf(verb);
    if (idx === -1) continue;
    const rest = normalized.slice(idx + verb.length).trim();
    const end = /[,.;!?]|\b(i|a|ale|oraz|pilne|pilna|awaria)\b/.exec(rest);
    const cut = end ? rest.slice(0, end.index).trim() : rest;
    return `${verb} ${cut}`.trim();
  }
  return undefined;
}

function extractDate(text: string): string | undefined {
  const normalized = text.toLowerCase();
  const ascii = toAscii(normalized);

  const relative = Object.keys(productionDictionaries.shared.relativeDates);
  for (const r of relative) {
    if (normalized.includes(r) || ascii.includes(toAscii(r))) {
      return r;
    }
  }

  for (const day of productionDictionaries.shared.weekdays) {
    const preps = ["do", "na", "w", "z", "we"];
    const forms = [day, toAscii(day)];
    for (const form of forms) {
      if (normalized.includes(form)) {
        for (const prep of preps) {
          const phrase = `${prep} ${form}`;
          if (normalized.includes(phrase)) return phrase;
        }
        return form;
      }
    }
  }

  return undefined;
}

const arrangementMarkers = [" ma ", " musi ", " musze ", " muszę ", " powinien ", " powinna ", " trzeba "];

function extractArrangement(text: string): string | undefined {
  const normalized = text.toLowerCase();
  const verbs = new Set<string>();
  for (const p of productionDictionaries.shared.arrangementPatterns) {
    const parts = p.toLowerCase().split(" ");
    if (parts.length >= 2) verbs.add(parts[parts.length - 1]);
  }
  verbs.add("zamówić");

  for (const marker of arrangementMarkers) {
    const idx = normalized.indexOf(marker);
    if (idx === -1) continue;
    const rest = normalized.slice(idx + marker.length).trim();
    const firstWord = rest.split(/\s+/)[0];
    if (!verbs.has(firstWord)) continue;
    const end = /[;!?.]|\b(ale|lub|oraz|i)\b/.exec(rest);
    const cut = end ? rest.slice(0, end.index).trim() : rest;
    return cut.replace(/^zrobić\s+/, "").trim();
  }
  return undefined;
}

const priorityMap: Record<string, string> = {
  "pilne": "pilne",
  "pilna": "pilne",
  "pilny": "pilne",
  "natychmiast": "natychmiast",
  "natychmiastowy": "natychmiast",
  "natychmiastowa": "natychmiast",
  "awaryjny": "pilne",
  "awaryjna": "pilne"
};

function extractPriority(text: string): string | undefined {
  const normalized = text.toLowerCase();
  const ascii = toAscii(normalized);
  for (const p of Object.keys(priorityMap)) {
    if (normalized.includes(p) || ascii.includes(toAscii(p))) {
      return priorityMap[p];
    }
  }
  return undefined;
}

const topicRules: { test: (arr: string, raw: string) => boolean; topic: string }[] = [
  { test: (a) => a.includes("zamówić"), topic: "zamówienie" },
  { test: (a, r) => a.includes("dostarczyć") || r.includes("dostaw"), topic: "dostawa" },
  { test: (a) => a.includes("sprawdzić"), topic: "kontrola jakości" },
  { test: (a) => a.includes("zgłosić"), topic: "magazyn" },
  { test: (a) => a.includes("wymienić"), topic: "konserwacja" },
  { test: (a) => a.includes("dopilnować"), topic: "logistyka" },
  { test: (a) => a.includes("przegląd"), topic: "przegląd" },
  { test: (a, r) => a.includes("kontrol") || r.includes("kontrol"), topic: "kontrola jakości" },
  { test: (a) => a.includes("przygotować") && a.includes("raport"), topic: "raport" },
  { test: (a) => a.includes("przygotować"), topic: "produkcja" },
  { test: (_, r) => r.includes("reklamacja"), topic: "reklamacja" },
  { test: (_, r) => r.includes("wada jakości"), topic: "wada jakości" }
];

function extractTopic(tokens: string[], text: string, dict: ReturnType<typeof buildDictionarySet>): string | undefined {
  const match = findLongestMatch(tokens, dict.topics, 3);
  if (match) return match.value;

  const normalized = text.toLowerCase();
  const arrangement = extractArrangement(text)?.toLowerCase() ?? "";
  for (const rule of topicRules) {
    if (rule.test(arrangement, normalized)) return rule.topic;
  }

  return undefined;
}

export async function extractEntities(
  text: string,
  industry: "galvo" | "kosbud",
  plantId?: string,
  managerId?: number
): Promise<ExtractedEntities> {
  const tokens = tokenize(text);
  const dict = buildDictionarySet(industry);
  const employees = buildEmployeeMap();
  const dictEntities = await matchDictionaries(text, industry, plantId, managerId);

  return {
    employee: extractEmployee(tokens, employees),
    equipment: dictEntities.equipment || extractEquipment(tokens, dict),
    location: extractLocation(tokens, dict),
    failure: dictEntities.failure || extractFailureFromDictionary(tokens, dict) || extractFailureFallback(text),
    arrangement: extractArrangement(text),
    date: extractDate(text),
    priority: extractPriority(text),
    topic: dictEntities.topic || extractTopic(tokens, text, dict)
  };
}
